import * as A from 'fp-ts/Array';
import { pipe } from 'fp-ts/lib/function';

import * as React from 'react';
import { BackLink } from '../../components/BackLink';
import { TextLink } from '../../components/ClickableText';
import {
  FullMatchFragment,
  VoteTypeFragmentFragment,
} from '../../generated/graphql';
import { paths } from '../../paths';
import { isNil } from '../../utils/isNil';
import { byName } from '../../utils/sorting';

import { useAlreadyCastVotes } from './useAlreadyCastVotes';
import { VoteForm } from './VoteForm';
import { getVoteLabel } from './voteLabelLookup';

interface Props {
  match: FullMatchFragment;
  voteType: VoteTypeFragmentFragment;
}

export const VoteWizard: React.FC<Props> = ({ match, voteType }) => {
  const { alreadyCastVotes } = useAlreadyCastVotes(match.id);

  const availableVoteTypes = match.team.voteTypes
    .filter((t) => t.enabled)
    .map((t) => t.id);

  const hasAlreadyCast = alreadyCastVotes.includes(voteType.id);

  const warningMessage = A.isEmpty(availableVoteTypes)
    ? 'Voting is currently disabled.'
    : !availableVoteTypes.includes(voteType.id)
    ? `The vote type "${voteType}" is not available for ${match.team.name}.`
    : hasAlreadyCast
    ? `You've already cast your "${getVoteLabel(
        voteType.name,
      )}" vote for this match.`
    : null;

  if (!isNil(warningMessage)) {
    return (
      <p className="text-center">
        {warningMessage}{' '}
        <TextLink to={paths.match.votes.vote(match.id)}>Go back</TextLink>
      </p>
    );
  }

  /**
   * If a squad has been selected for this match, only show those players
   * on the vote form. Otherwise just show the whole squad
   */
  const players = A.isEmpty(match.players) ? match.team.players : match.players;

  return (
    <div className="space-y-4">
      <BackLink to={paths.match.votes.vote(match.id)}>Back</BackLink>
      <VoteForm
        matchId={match.id}
        players={pipe(players, A.sort(byName))}
        voteType={voteType}
      />
    </div>
  );
};
